import { useEffect } from "react";

export const BUSINESS = {
  name: "Sitebound",
  tagline: "Australian Construction & Civil Careers",
  abn: "73 130 456 155",
  email: "hello@sitebound.com.au",
  url: "https://sitebound.com.au",
};

export function usePageTitle(title, description) {
  useEffect(() => {
    document.title = title;
    if (description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", description);
    }
    // self-referencing canonical + per-page Open Graph (SPA: set at render —
    // the same pipeline Google reads for titles/descriptions)
    const path = window.location.pathname;
    const canonical = "https://sitebound.com.au" + (path === "/" ? "/" : path.replace(/\/$/, ""));
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonical);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    if (description) {
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", description);
    }
  }, [title, description]);
}

export const MARQUEE_ITEMS = [
  "White Card",
  "High Risk Licences",
  "FIFO Roster Guides",
  "Resume Templates",
  "Interview Blueprints",
  "Mining Recruitment",
  "Site Checklists",
  "Cover Letters",
];

export const MANIFESTO = [
  {
    num: "01",
    title: "Built for the industry",
    body: "Everything in a Sitebound pack is written for Australian construction, civil, mining and FIFO hiring — not recycled corporate career advice. Site supervisors, labour hire coordinators and recruiters scan for different things. We build for them.",
  },
  {
    num: "02",
    title: "Made for first-timers",
    body: "No site time? No problem. Every guide assumes you're starting from zero — which tickets to get first, where the entry-level jobs actually come from, and how to sound like you belong on a crew before you've been on one.",
  },
  {
    num: "03",
    title: "Job-ready, not just templates",
    body: "A resume template alone doesn't get you hired. Sitebound packs combine the documents, the application system, the ticket roadmap and the interview prep — the full path from 'thinking about it' to walking on site.",
  },
];

export const FAQS = [
  {
    q: "How do I receive my pack after buying?",
    a: "Instantly. As soon as your payment clears you get access to your Sitebound Job Seeker Hub — every guide readable online, on any device — plus a complete ZIP backup you can download from inside your Hub anytime. Create a free account with your purchase email and verify it to unlock the Hub.",
  },
  {
    q: "What's inside the Complete Construction Job Seeker Pack?",
    a: "95 resources organised into 10 sections: getting started (including apprenticeships), resume & cover letter templates (editable DOCX) with annotated examples, finding construction work, tickets & licences (including the Mobile Plant & Machinery Guide), interview preparation with sample answers, site basics (including D&A testing, bullying escalation and working in heat), FIFO starter guide, pay, tax, awards & job offers, first day & first week, and a master templates & checklists folder (editable DOCX and XLSX trackers). Everything is readable online in your Sitebound Job Seeker Hub, with editable files downloadable individually.",
  },
  {
    q: "Who is Sitebound for?",
    a: "People trying to get their first job in Australian construction, civil, mining or FIFO work — school leavers, career changers, and workers coming across from other industries. It's built for entry-level, not for experienced supervisors.",
  },
  {
    q: "Do I need experience or tickets before I buy?",
    a: "No. The pack is designed for people with no site experience. The tickets and licences guide shows you exactly what to get first (like your White Card), what it costs, and what can wait until you're earning.",
  },
  {
    q: "Is this a subscription?",
    a: "No. It's a one-time payment of $49 AUD. Pay once, download once, keep it forever.",
  },
  {
    q: "What format are the files in?",
    a: "Read every guide online in your Job Seeker Hub (or download the PDFs to print). Resume, cover letter and worksheet templates are editable DOCX files, and the trackers are editable XLSX spreadsheets. A complete ZIP backup is always available from your account.",
  },
  {
    q: "Can I get a refund?",
    a: "Because it's a digital product delivered instantly, change-of-mind refunds aren't offered once the pack has been downloaded. Nothing in our refund policy limits your rights under Australian Consumer Law — if the product is faulty or not as described, you're covered. See our Refund Policy for details.",
  },
  {
    "q": "Will this guarantee me a job?",
    "a": "No honest provider can guarantee that. What the pack does is make you job-ready: the right documents, the right tickets roadmap, and the knowledge to apply and interview with confidence. The rest is on the application — and we help with that too.",
  },
  {
    q: "Can I get into FIFO with no mining experience?",
    a: "Yes — plenty of people do, and the FIFO guidance in the pack covers the realistic entry pathways, what rosters actually look like, and what recruiters want to see from first-timers.",
  },
  {
    q: "Do I need an account to buy?",
    a: "No. Guest checkout works fine. After your purchase, you'll be able to access your purchased resources through your Job Seeker Hub — we'll email you a link to get started. Creating a free account with your purchase email lets you open your Hub, re-download your files and see your order history anytime.",
  },
];
